import { ProgramType, type Device, type Program } from "../../state/api.types";

import { Tooltip, TooltipTrigger, cx } from "@light-technology/components";
import { AlertCircle, BatteryFull, Car02 } from "@untitledui/icons";
import {
  findProgramDevice,
  getIsMissing,
  getNeedsAttention,
  getProgramDevices,
} from "../../utilities/devices";

type DeviceStatusProps = {
  devices: Device[];
  programs: Program[];
};

export const DeviceStatuses = (props: DeviceStatusProps) => {
  const programDevices = getProgramDevices({
    programs: props.programs,
    devices: props.devices,
  });

  if (props.programs.length === 0) return null;

  return (
    <div className="isolate flex">
      {[...programDevices.entries()].map(([program, devices], i) => {
        return (
          <DeviceStatusIcon
            key={program.uuid}
            program={program}
            devices={devices}
            className={cx(
              i === 0 && "z-4",
              i === 1 && "z-3",
              i === 2 && "z-2",
              i === 3 && "z-1",
              i >= 4 && "z-0",
              i > 0 && "-ml-sm",
              "bg-primary",
            )}
          />
        );
      })}
    </div>
  );
};

export const DeviceIcon = ({
  isMissing,
  needsAttention,
  programType,
  className,
}: {
  isMissing: boolean;
  needsAttention: boolean;
  programType: ProgramType;
  className?: string;
}) => {
  let device;

  switch (programType) {
    case ProgramType.Battery:
      device = (
        <BatteryFull
          className={cx(
            "size-4 rotate-270",
            isMissing ? "stroke-fg-quaternary" : "stroke-fg-secondary",
          )}
        />
      );
      break;
    case ProgramType.Evcharging:
      device = (
        <Car02
          className={cx(
            "size-4",
            isMissing ? "stroke-fg-quaternary" : "stroke-fg-secondary",
          )}
        />
      );
      break;
  }

  return (
    <div
      className={cx(
        "relative rounded-full border border-secondary p-md",
        isMissing && "border-dashed",
        className,
      )}
    >
      {device}

      {needsAttention && (
        <AlertCircle className="absolute right-0 bottom-0 size-3 fill-utility-warning-200 stroke-utility-warning-700" />
      )}
    </div>
  );
};

const DeviceStatusIcon = ({
  program,
  devices,
  className,
}: {
  program: Program;
  devices: Device[];
  className?: string;
}) => {
  const program_type = program.program_type;

  const device = findProgramDevice(devices);

  const isMissing: boolean = getIsMissing(device);
  const needsAttention: boolean = getNeedsAttention(device);

  let tooltip = "";
  switch (program_type) {
    case ProgramType.Battery:
      tooltip = isMissing
        ? "No battery connected to battery plan"
        : needsAttention
          ? "Battery device needs attention"
          : "Connected battery device";
      break;
    case ProgramType.Evcharging:
      tooltip = isMissing
        ? "No EV or charger connected to charging plan"
        : needsAttention
          ? "EV charging device needs attention"
          : "Connected EV charging device";
  }

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <TooltipTrigger>
        <DeviceIcon
          isMissing={isMissing}
          needsAttention={needsAttention}
          programType={program_type}
          className={className}
        />
      </TooltipTrigger>
    </Tooltip>
  );
};
